import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Wrench, User, MapPin, Clock, Leaf, CheckCircle, ArrowLeft, Folder } from "lucide-react";

const DURATIONS = [
  { key: "2h", h: 2 },
  { key: "4h", h: 4 },
  { key: "8h", h: 8 },
  { key: "1d", h: 24 },
  { key: "3d", h: 72 },
  { key: "1w", h: 168 },
];

export default function QuickBook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [machine, setMachine] = useState(null);
  const [projects, setProjects] = useState([]);
  const [bokare, setBokare] = useState("");
  const [projectId, setProjectId] = useState("");
  const [platsText, setPlatsText] = useState("");
  const [durationKey, setDurationKey] = useState("4h");
  const [customH, setCustomH] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: ps }] = await Promise.all([
        api.get(`/machines/${id}`),
        api.get("/projects"),
      ]);
      setMachine(m);
      setProjects(ps || []);
    } catch {
      toast.error(t("detail.notFound"));
      navigate("/machines");
    }
  }, [id, navigate, t]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    setBokare(user.name || "");
    load();
  }, [loading, user, load, navigate]);

  if (loading || !user) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }
  if (!machine) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }

  const hours = durationKey === "custom" ? Math.max(1, Number(customH || 1)) : (DURATIONS.find((d) => d.key === durationKey)?.h || 4);
  const start = new Date();
  const slut = new Date(start.getTime() + hours * 60 * 60 * 1000);

  // CO2e preview (assume 0 km transport for quick-book — visible km on detail booking)
  const co2e = machine.co2e_indikator || {};
  const besparing = co2e.co2e_besparing_kg ?? 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!bokare.trim()) {
      toast.error(t("qb.q1Hint"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/machine-bookings", {
        machine_id: machine.id,
        start: start.toISOString(),
        slut: slut.toISOString(),
        transport_km: 0,
        project_id: projectId || null,
        bokare_namn: bokare.trim(),
        plats_text: platsText.trim(),
        note: "Snabbboka via QR",
      });
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("mbk.collision"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <div className="max-w-[700px] mx-auto px-6 pt-16 pb-16 text-center" data-testid="quickbook-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-[var(--moss)] text-[var(--bone)] grid place-items-center">
            <CheckCircle size={36} />
          </div>
          <h1 className="font-display text-3xl mt-6">{t("qb.success")}</h1>
          <p className="mt-3 text-[var(--ink-700)]">{t("qb.successDetail")}</p>

          <div className="mt-8 border border-[var(--ink-200)] bg-[var(--bone)] p-5 text-left">
            <div className="label-tech">{t("qb.summary")}</div>
            <div className="mt-3 space-y-2 text-sm">
              <Row icon={<Wrench size={14} />} k={t("mach.namn")} v={machine.namn} />
              <Row icon={<User size={14} />} k={t("qb.q1")} v={done.bokare_namn} />
              <Row icon={<MapPin size={14} />} k={t("qb.q2")} v={done.plats_text || projects.find((p) => p.id === done.project_id)?.namn || "—"} />
              <Row icon={<Clock size={14} />} k={t("qb.from")} v={new Date(done.start).toLocaleString()} />
              <Row icon={<Clock size={14} />} k={t("qb.to")} v={new Date(done.slut).toLocaleString()} />
              <Row icon={<Leaf size={14} />} k={t("mbk.savings")} v={`~${done.beraknad_co2e_besparing_kg} kg CO2e`} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button onClick={() => { setDone(null); }} className="btn-ghost" data-testid="quickbook-again-btn">{t("qb.bookAnother")}</button>
            <Link to={`/machines/${machine.id}`} className="btn-primary" data-testid="quickbook-view-detail-btn">{t("qb.viewDetail")}</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[700px] mx-auto px-6 pt-10 pb-16" data-testid="quickbook-page">
        <Link to={`/machines/${machine.id}`} className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="quickbook-back-link">
          <ArrowLeft size={12} /> {t("nav.backToList")}
        </Link>

        <div className="mt-4">
          <div className="label-tech inline-flex items-center gap-2"><Wrench size={12} /> {machine.typ} · {machine.namn}</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-2">{t("qb.title")}</h1>
          <p className="mt-2 text-[var(--ink-700)]">{t("qb.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          {/* Q1: Vem */}
          <Section icon={<User size={16} />} step="01" title={t("qb.q1")}>
            <input
              type="text"
              className="input-tech"
              value={bokare}
              onChange={(e) => setBokare(e.target.value)}
              placeholder={t("qb.q1Hint")}
              required
              data-testid="quickbook-vem-input"
            />
          </Section>

          {/* Q2: Var */}
          <Section icon={<MapPin size={16} />} step="02" title={t("qb.q2")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="label-tech mb-2"><Folder size={10} className="inline" /> {t("qb.q2Project")}</div>
                <select className="input-tech" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="quickbook-project-select">
                  <option value="">—</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
                </select>
              </div>
              <div>
                <div className="label-tech mb-2">{t("qb.q2Location")}</div>
                <input type="text" className="input-tech" value={platsText} onChange={(e) => setPlatsText(e.target.value)} data-testid="quickbook-plats-input" />
              </div>
            </div>
          </Section>

          {/* Q3: Hur länge */}
          <Section icon={<Clock size={16} />} step="03" title={t("qb.q3")}>
            <div className="flex flex-wrap gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDurationKey(d.key)}
                  className={`px-4 py-2 border border-[var(--ink-200)] text-sm font-mono-tech uppercase tracking-[0.1em] ${durationKey === d.key ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)] hover:bg-[var(--surface-alt)]"}`}
                  data-testid={`quickbook-duration-${d.key}`}
                >
                  {t(`qb.duration${d.key}`)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDurationKey("custom")}
                className={`px-4 py-2 border border-[var(--ink-200)] text-sm font-mono-tech uppercase tracking-[0.1em] ${durationKey === "custom" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)] hover:bg-[var(--surface-alt)]"}`}
                data-testid="quickbook-duration-custom"
              >
                {t("qb.durationCustom")}
              </button>
            </div>
            {durationKey === "custom" && (
              <input
                type="number"
                min="1"
                step="1"
                className="input-tech mt-3 max-w-[200px]"
                value={customH}
                onChange={(e) => setCustomH(e.target.value)}
                data-testid="quickbook-custom-hours-input"
              />
            )}
            <div className="label-tech mt-3">
              {t("qb.from")}: {start.toLocaleString()} → {t("qb.to")}: {slut.toLocaleString()}
            </div>
          </Section>

          {/* CO2e info */}
          <div className="p-4 border border-[var(--moss)] bg-[var(--moss)] text-[var(--bone)]" data-testid="quickbook-co2e">
            <div className="label-tech opacity-80 inline-flex items-center gap-2"><Leaf size={12} /> {t("mbk.savings")}</div>
            <div className="font-display text-2xl mt-1">~{besparing} kg CO2e</div>
            <div className="label-tech opacity-80 mt-1">{t("mbk.savingsCompare")}</div>
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center py-4 text-base" data-testid="quickbook-confirm-btn">
            {submitting ? "…" : <><CheckCircle size={16} /> {t("qb.confirm")}</>}
          </button>
        </form>
      </div>
    </Layout>
  );
}

function Section({ icon, step, title, children }) {
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5">
      <div className="flex items-center gap-3 mb-4">
        <span className="label-tech">[ {step} ]</span>
        <span className="font-display text-lg inline-flex items-center gap-2">{icon} {title}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ icon, k, v }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[var(--ink-500)]">{icon}</span>
      <span className="label-tech">{k}:</span>
      <span className="font-display">{v}</span>
    </div>
  );
}
